import { Link } from "@tanstack/react-router";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useWorkspaceEnabled, useWorkspaceNavigate } from "@/features/workspaces";

interface AppLinkProps {
  to: string;
  params?: Record<string, string>;
  search?: Record<string, string>;
  title?: string;
  className?: string;
  children: React.ReactNode;
}

/**
 * Unified link component that works in both workspace and normal modes.
 *
 * In workspace mode: renders a <button> that navigates within the active tab.
 * In normal mode: renders a TanStack Router <Link>.
 */
export function AppLink({ to, params, search, title, className, children }: AppLinkProps) {
  const isWorkspacesEnabled = useWorkspaceEnabled();
  const workspaceNavigate = useWorkspaceNavigate();
  const workspaceParams = search ? { ...params, ...search } : params;

  if (isWorkspacesEnabled) {
    return (
      <Button
        type="button"
        variant="ghost"
        onClick={() => workspaceNavigate({ route: to, params: workspaceParams, title })}
        className={cn(
          "h-auto justify-start px-0 py-0 font-[inherit] text-[inherit] shadow-none hover:bg-transparent",
          className
        )}
      >
        {children}
      </Button>
    );
  }

  return (
    <Link to={to as "/"} params={params} search={search} className={className}>
      {children}
    </Link>
  );
}
